import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, CreditCard, Search, Grid, List, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateLocal } from '@/utils/helpers';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    client_id: '',
    subscription_type: 'monthly',
    price: '',
    payment_method: 'cash',
    start_date: '',
  });

  useEffect(() => {
    fetchSubscriptions();
    fetchClients();
  }, []);

  useEffect(() => {
    let filtered = subscriptions;
    
    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(sub => sub.status === activeTab);
    }
    
    // Filter by search
    if (searchQuery.trim()) {
      filtered = filtered.filter(sub => {
        const clientName = getClientName(sub.client_id).toLowerCase();
        return clientName.includes(searchQuery.toLowerCase());
      });
    }
    
    setFilteredSubscriptions(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchQuery, subscriptions, activeTab]);

  // Calculate pagination
  const totalPages = itemsPerPage === 'all' 
    ? 1 
    : Math.ceil(filteredSubscriptions.length / itemsPerPage);
  
  const paginatedSubscriptions = itemsPerPage === 'all' 
    ? filteredSubscriptions 
    : filteredSubscriptions.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === 'all' ? 'all' : parseInt(value));
    setCurrentPage(1);
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get('/subscriptions');
      setSubscriptions(response.data);
      // Update statuses
      await axios.post('/subscriptions/update-statuses');
    } catch (error) {
      toast.error('Error al cargar suscripciones');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const handleDelete = async (subscriptionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta suscripción? Esta acción no se puede deshacer.')) return;

    try {
      await axios.delete(`/subscriptions/${subscriptionId}`);
      toast.success('Suscripción eliminada exitosamente');
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar suscripción');
    }
  };

  const isAdmin = () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    try {
      const user = JSON.parse(userStr);
      return user.role === 'admin';
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/subscriptions', {
        ...formData,
        price: parseFloat(formData.price),
      });

      toast.success('Suscripción creada');
      setIsDialogOpen(false);
      resetForm();
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear suscripción');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      subscription_type: 'monthly',
      price: '',
      payment_method: 'cash',
      start_date: '',
    });
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Cliente no encontrado';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'expiring_soon': return 'status-expiring';
      case 'expired': return 'status-expired';
      default: return '';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activa';
      case 'expiring_soon': return 'Por Vencer';
      case 'expired': return 'Vencida';
      default: return status;
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'daily': return 'Diario';
      case '15days': return '15 Días';
      case 'monthly': return 'Mensual';
      default: return type;
    }
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 'cash': return 'Efectivo';
      case 'card': return 'Tarjeta';
      case 'check': return 'Cheque';
      case 'transfer': return 'Transferencia';
      default: return method || 'Efectivo';
    }
  };

  return (
    <div className="subscriptions-page" data-testid="subscriptions-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Suscripciones</h1>
          <p className="page-subtitle">Gestiona las membresías de clientes</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} data-testid="add-subscription-btn">
          <Plus size={20} />
          Nueva Suscripción
        </Button>
      </div>

      <Card className="search-card">
        <div className="search-controls">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <Input
              type="text"
              placeholder="Buscar por nombre de cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              data-testid="search-subscriptions-input"
            />
          </div>
          <div className="view-toggle">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-btn"
            >
              <Grid size={18} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              data-testid="list-view-btn"
            >
              <List size={18} />
            </Button>
          </div>
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="subscriptions-tabs">
        <TabsList className="tabs-list">
          <TabsTrigger value="active" data-testid="tab-active">Activas</TabsTrigger>
          <TabsTrigger value="expiring_soon" data-testid="tab-expiring">Por Vencer</TabsTrigger>
          <TabsTrigger value="expired" data-testid="tab-expired">Vencidas</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">Todas</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="tab-content">
          {viewMode === 'grid' ? (
            <div className="subscriptions-grid">
            {paginatedSubscriptions.map((subscription) => (
              <Card key={subscription.id} className="subscription-card" data-testid={`subscription-card-${subscription.id}`}>
                <div className="subscription-header">
                  <h3 className="client-name">{getClientName(subscription.client_id)}</h3>
                  <span className={`status-badge ${getStatusBadgeClass(subscription.status)}`}>
                    {getStatusText(subscription.status)}
                  </span>
                </div>

                <div className="subscription-details">
                  <div className="detail-item">
                    <span className="detail-label">Tipo:</span>
                    <span className="detail-value">{getTypeText(subscription.subscription_type)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Precio:</span>
                    <span className="detail-value">${subscription.price.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Pago:</span>
                    <span className="detail-value">{getPaymentMethodText(subscription.payment_method)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Inicio:</span>
                    <span className="detail-value">{formatDateLocal(subscription.start_date)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Vence:</span>
                    <span className="detail-value">{formatDateLocal(subscription.end_date)}</span>
                  </div>
                </div>

                <div className="subscription-footer">
                  <span className="created-by">Por: {subscription.created_by}</span>
                  {isAdmin() && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(subscription.id)}
                      className="delete-btn"
                      title="Eliminar suscripción"
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
            </div>
          ) : (
            <Card className="table-card">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Tipo</th>
                      <th>Precio</th>
                      <th>Método Pago</th>
                      <th>Inicio</th>
                      <th>Vencimiento</th>
                      <th>Estado</th>
                      <th>Creado por</th>
                      {isAdmin() && <th>Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSubscriptions.map((subscription) => (
                      <tr key={subscription.id} data-testid={`subscription-row-${subscription.id}`}>
                        <td><strong>{getClientName(subscription.client_id)}</strong></td>
                        <td>{getTypeText(subscription.subscription_type)}</td>
                        <td className="table-price">${subscription.price.toFixed(2)}</td>
                        <td>{getPaymentMethodText(subscription.payment_method)}</td>
                        <td>{formatDateLocal(subscription.start_date)}</td>
                        <td>{formatDateLocal(subscription.end_date)}</td>
                        <td>
                          <span className={`status-badge ${getStatusBadgeClass(subscription.status)}`}>
                            {getStatusText(subscription.status)}
                          </span>
                        </td>
                        <td>{subscription.created_by}</td>
                        {isAdmin() && (
                          <td>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(subscription.id)}
                              title="Eliminar suscripción"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Pagination Controls */}
          {filteredSubscriptions.length > 0 && (
            <Card className="pagination-card">
              <div className="pagination-controls">
                <div className="items-per-page">
                  <Label htmlFor="items-per-page">Elementos por página:</Label>
                  <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                    <SelectTrigger className="items-per-page-select" data-testid="items-per-page-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="all">Todos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {itemsPerPage !== 'all' && (
                  <div className="pagination-info">
                    <span>
                      Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredSubscriptions.length)} de {filteredSubscriptions.length}
                    </span>
                  </div>
                )}

                {itemsPerPage !== 'all' && totalPages > 1 && (
                  <div className="pagination-buttons">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      data-testid="prev-page-btn"
                    >
                      Anterior
                    </Button>

                    <div className="page-numbers">
                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              data-testid={`page-${page}-btn`}
                            >
                              {page}
                            </Button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return <span key={page} className="page-ellipsis">...</span>;
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      data-testid="next-page-btn"
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          )}

          {filteredSubscriptions.length === 0 && (
            <div className="empty-state">
              <CreditCard size={64} />
              <h3>No hay suscripciones</h3>
              <p>Las suscripciones aparecerán aquí</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="subscription-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Suscripción</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="subscription-form">
            <div className="form-group">
              <Label htmlFor="client">Cliente*</Label>
              <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})}>
                <SelectTrigger data-testid="client-select">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="type">Tipo de Suscripción*</Label>
              <Select value={formData.subscription_type} onValueChange={(value) => setFormData({...formData, subscription_type: value})}>
                <SelectTrigger data-testid="type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Diario</SelectItem>
                  <SelectItem value="15days">15 Días</SelectItem>
                  <SelectItem value="monthly">Mensual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="payment_method">Método de Pago*</Label>
              <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="card">Tarjeta</SelectItem>
                  <SelectItem value="check">Cheque</SelectItem>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="price">Precio*</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                required
                data-testid="price-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="start_date">Fecha de Inicio (opcional)</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                data-testid="start-date-input"
              />
              <p className="form-hint">Si no se especifica, se usará la fecha actual</p>
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-subscription-btn">
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .subscriptions-page {
          max-width: 1400px;
        }

        .subscription-dialog {
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .subscriptions-tabs {
          margin-top: 2rem;
        }

        .tabs-list {
          background: white;
          padding: 0.5rem;
          border-radius: 10px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .tab-content {
          margin-top: 2rem;
        }

        .subscriptions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .subscription-card {
          padding: 1.5rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.3s;
        }

        .subscription-card:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .subscription-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .client-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
        }

        .status-badge {
          padding: 0.375rem 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-active {
          background: #d1fae5;
          color: #065f46;
        }

        .status-expiring {
          background: #fed7aa;
          color: #92400e;
        }

        .status-expired {
          background: #fee2e2;
          color: #991b1b;
        }

        .subscription-details {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .detail-label {
          font-size: 0.9375rem;
          color: #64748b;
          font-weight: 500;
        }

        .detail-value {
          font-size: 0.9375rem;
          color: #1e293b;
          font-weight: 600;
        }

        .subscription-footer {
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .created-by {
          font-size: 0.875rem;
          color: #64748b;
        }

        .delete-btn {
          padding: 0.5rem;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
          color: #64748b;
        }

        .subscription-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-top: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-hint {
          font-size: 0.875rem;
          color: #64748b;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 1rem;
        }

        .search-card {
          padding: 1rem;
          margin-bottom: 2rem;
        }

        .search-controls {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: #64748b;
        }

        .search-input {
          padding-left: 3rem;
        }

        .view-toggle {
          display: flex;
          gap: 0.5rem;
        }

        .table-card {
          padding: 0;
          overflow: hidden;
        }

        .table-container {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table thead {
          background: #f8fafc;
        }

        .data-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.875rem;
          color: #64748b;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        .data-table td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.9375rem;
        }

        .data-table tbody tr {
          transition: background 0.2s;
        }

        .data-table tbody tr:hover {
          background: #f8fafc;
        }

        .table-price {
          color: #10b981;
          font-weight: 700;
        }

        .pagination-card {
          margin-top: 2rem;
          padding: 1.5rem;
        }

        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .items-per-page {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .items-per-page-select {
          width: 120px;
        }

        .pagination-info {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .pagination-buttons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .page-numbers {
          display: flex;
          gap: 0.25rem;
        }

        .page-ellipsis {
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          color: #64748b;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
          }

          .subscriptions-grid {
            grid-template-columns: 1fr;
          }

          .search-controls {
            flex-direction: column;
          }

          .view-toggle {
            width: 100%;
          }

          .view-toggle button {
            flex: 1;
          }

          .pagination-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .items-per-page {
            justify-content: space-between;
          }

          .pagination-buttons {
            flex-direction: column;
            width: 100%;
          }

          .page-numbers {
            justify-content: center;
            flex-wrap: wrap;
          }

          .pagination-info {
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Subscriptions;
